<template>
  <InputSelect
    v-model="selectedIntegrationId"
    emit-value
    new-design
    option-label="label"
    option-prop="id"
    :options="platformIntegrations"
  >
    <template #prepend>
      <div
        v-if="selectedOrganisationSocialIntegration"
        class="tw:flex tw:min-h-[44px] tw:items-center"
      >
        <SocialProfileIcon
          data-testid="social-profile-icon"
          :platform-icon="selectedOrganisationSocialIntegration.platformIcon()"
          :profile-url="
            selectedOrganisationSocialIntegration.metadata?.profilePhotoUrl
          "
          :size="24"
        />
      </div>
    </template>
    <template #option="{ option: integration, active }">
      <div
        class="tw:flex tw:cursor-pointer tw:items-center tw:px-3 tw:py-2 tw:hover:bg-denim-200"
        :class="{ 'tw:bg-denim-100': active }"
      >
        <SocialProfileIcon
          :platform-icon="integration.platformIcon()"
          :profile-url="integration.metadata?.profilePhotoUrl"
          :size="24"
        />
        <span class="tw:px-1.5 tw:text-denim-900">{{ integration.label }}</span>
      </div>
    </template>
  </InputSelect>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";

import { InputSelect } from "shared/components/base";
import SocialProfileIcon from "shared/components/SocialProfileIcon.vue";
import type OrganisationSocialIntegration from "shared/resources/OrganisationSocialIntegration";
import { useSocialIntegrationsStore } from "shared/stores/socialIntegrations";
import { type Nullable } from "shared/types";

interface ManageSocialConnectionsSelectIntegrationProps {
  platform: OrganisationSocialIntegration["socialPlatform"];
}

const props = defineProps<ManageSocialConnectionsSelectIntegrationProps>();

const selectedIntegrationId = defineModel<Nullable<string>>();

const { integrationsBySocialPlatform } = useSocialIntegrationsStore();

const platformIntegrations = computed(() =>
  Object.values(integrationsBySocialPlatform[props.platform] || {})
);

const selectedOrganisationSocialIntegration = computed(
  () =>
    integrationsBySocialPlatform[props.platform]?.[
      selectedIntegrationId.value ?? ""
    ] ?? null
);

watch(
  platformIntegrations,
  (newIntegrations) => {
    if (newIntegrations.length > 0 && !selectedIntegrationId.value) {
      selectedIntegrationId.value = newIntegrations[0].id;
    }
  },
  { immediate: true }
);

defineExpose({
  platformIntegrations,
  selectedIntegrationId,
  selectedOrganisationSocialIntegration,
});
</script>
